import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Upload, CheckCircle2, Loader2, Store } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { businessCategories } from "../../data/content";
import { createDoc, uploadImage, logActivity } from "../../lib/store";

const inputCls =
  "w-full rounded-xl border border-[#0033FF]/12 bg-white px-4 py-3 text-[#0b1221] placeholder:text-[#9aa7bd] focus:outline-none focus:ring-2 focus:ring-[#0033FF] focus:border-transparent transition-shadow duration-200";

const empty = {
  shopName: "",
  ownerName: "",
  mobile: "",
  whatsapp: "",
  email: "",
  category: "",
  address: "",
  mapLocation: "",
};

export const PartnerForm = () => {
  const [form, setForm] = useState(empty);
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.shopName || !form.ownerName || !form.mobile || !form.category) {
      toast.error("Please fill all required fields.");
      return;
    }
    if (!/^\+?[0-9\s-]{7,15}$/.test(form.mobile)) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const photoUrl = photo ? await uploadImage(photo, "partners") : "";
      await createDoc("partners", {
        ...form,
        photoUrl,
        status: "pending",
        notes: "",
      });
      await logActivity("partner", `New partner application from ${form.shopName}`);
      setDone(true);
      setForm(empty);
      setPhoto(null);
      toast.success("Application submitted successfully! Our team will reach out soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="partner"
      className="relative py-24 md:py-32 bg-[#f4f7fb]"
      data-testid="partner-section"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <span className="inline-flex w-14 h-14 rounded-2xl bg-[#0033FF] items-center justify-center text-white mb-6">
              <Store className="w-7 h-7" />
            </span>
            <p className="text-sm font-semibold text-[#0033FF] uppercase tracking-widest mb-3">
              Become a Partner
            </p>
            <h2 className="font-display font-extrabold tracking-tighter text-4xl md:text-5xl text-[#0b1221]">
              Grow your store with QuickDrop Go.
            </h2>
            <p className="mt-5 text-[#5e6c84] text-lg leading-relaxed">
              List your shop, reach thousands of nearby customers, and let our
              riders handle the delivery. Zero setup cost.
            </p>
          </div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-7 bg-white rounded-3xl p-8 md:p-10 border border-[#0033FF]/8 shadow-[0_20px_60px_rgba(0,51,255,0.08)] grid sm:grid-cols-2 gap-5"
            data-testid="partner-form"
          >
            <Field label="Shop Name *">
              <input className={inputCls} value={form.shopName} onChange={set("shopName")} placeholder="e.g. Fresh Mart" data-testid="partner-shopName" />
            </Field>
            <Field label="Owner Name *">
              <input className={inputCls} value={form.ownerName} onChange={set("ownerName")} placeholder="Full name" data-testid="partner-ownerName" />
            </Field>
            <Field label="Mobile Number *">
              <input className={inputCls} value={form.mobile} onChange={set("mobile")} placeholder="+91" data-testid="partner-mobile" />
            </Field>
            <Field label="WhatsApp Number">
              <input className={inputCls} value={form.whatsapp} onChange={set("whatsapp")} placeholder="+91" data-testid="partner-whatsapp" />
            </Field>
            <Field label="Email">
              <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@store.com" data-testid="partner-email" />
            </Field>
            <Field label="Business Category *">
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger className={inputCls + " h-[50px]"} data-testid="partner-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {businessCategories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Shop Address" full>
              <input className={inputCls} value={form.address} onChange={set("address")} placeholder="Street, area, city" data-testid="partner-address" />
            </Field>
            <Field label="Google Map Location" full>
              <input className={inputCls} value={form.mapLocation} onChange={set("mapLocation")} placeholder="Paste Google Maps link" data-testid="partner-map" />
            </Field>
            <Field label="Upload Shop Photo" full>
              <FileDrop file={photo} onChange={setPhoto} testid="partner-photo" accept="image/*" />
            </Field>

            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0033FF] text-white font-semibold py-4 rounded-xl hover:bg-[#0026bf] transition-colors duration-200 disabled:opacity-70"
                data-testid="partner-submit"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : done ? <CheckCircle2 className="w-5 h-5" /> : null}
                {loading ? "Submitting..." : done ? "Submitted" : "Submit Application"}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export const Field = ({ label, children, full }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""}`}>
    <span className="block text-sm font-medium text-[#0b1221] mb-2">{label}</span>
    {children}
  </label>
);

export const FileDrop = ({ file, onChange, testid, accept }) => (
  <div className="relative rounded-xl border-2 border-dashed border-[#0033FF]/20 bg-[#f4f7fb] px-4 py-6 text-center hover:border-[#0033FF]/50 transition-colors duration-200">
    <input
      type="file"
      accept={accept}
      onChange={(e) => onChange(e.target.files[0])}
      className="absolute inset-0 opacity-0 cursor-pointer"
      data-testid={testid}
    />
    <Upload className="w-6 h-6 text-[#0033FF] mx-auto mb-2" />
    <p className="text-sm text-[#5e6c84]">
      {file ? file.name : "Click to upload or drag & drop"}
    </p>
  </div>
);
